import React, { useEffect, useMemo, useRef, useState, useDeferredValue, useCallback } from "react";
import api from "../../constants/api";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import StatusBadge from "../ui/StatusBadge";
import TypBadge from "../ui/TypBadge";
import FalltypBadge from "../ui/FalltypBadge.jsx";
import CollapsibleBlock from "../ui/CollapsibleBlock";
import { FALLTYP_VALUES, FALLTYP_LABELS } from "../../constants/status.js";
import AppButton from "../ui/AppButton.jsx";
import { Input } from "../forms/shared/FormAtoms.jsx";
import { countFalltypAktivArchiv, formatFalltypAktivArchivHeader } from "./serviceFalltypCounts.js";

/* Archiv-Flag robust ermitteln */
const isArchiviert = x => {
  const s = (x?.status || x?.status_override || "").toString().toLowerCase().trim();
  return Boolean(x?.archiviert === true || x?.archived === true || x?.archived_at || x?.archiv_at || x?.archiviert_am || s === "archiviert");
};

/* Safe Date → timestamp */
const toTime = v => {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
};
export default function KundenBlockServiceauftraege({
  kundenId,
  objektId,
  compact = false,
  title = "🔧 Serviceaufträge",
}) {
  const [auftraege, setAuftraege] = useState([]);
  const [suche, setSuche] = useState("");
  const deferredSuche = useDeferredValue(suche);
  const [showArchiv, setShowArchiv] = useState(false);
  const [loading, setLoading] = useState(true);
  const [falltypFilter, setFalltypFilter] = useState("__all__");
  const navigate = useNavigate();
  const abortRef = useRef(null);

  /* Laden (race-safe) */
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!objektId && !kundenId) {
        if (mounted) {
          setAuftraege([]);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const url = objektId
          ? `/objekte/${objektId}/serviceauftraege`
          : `/kunden/${kundenId}/serviceauftraege`;
        const res = await api.get(url, {
          signal: controller.signal
        });
        const raw = Array.isArray(res.data) ? res.data : [];
        const fmt = new Intl.DateTimeFormat("de-CH", {
          dateStyle: "short",
          timeStyle: "short"
        });
        const enriched = raw.map(a => {
          const ts = toTime(a.updated_at || a.erstellt_am);
          const arch = isArchiviert(a);
          const dateStr = ts ? fmt.format(new Date(ts)) : "";
          const blob = `${a.id ?? ""} ${a.titel ?? a.name ?? ""} ${a.beschreibung ?? ""} ${a.status ?? ""} ${a.typ ?? ""} ${dateStr}`.toLowerCase();
          return {
            ...a,
            _ts: ts,
            _arch: arch,
            _date: dateStr,
            _blob: blob
          };
        });
        if (!controller.signal.aborted && mounted) setAuftraege(enriched);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        if (mounted) setAuftraege([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, [kundenId, objektId]);

  /* Suche & Sort: aktiv zuerst, dann nach Datum absteigend */
  const gefiltert = useMemo(() => {
    const q = (deferredSuche || "").toLowerCase().trim();
    let list = [...auftraege].sort((a, b) => {
      if (a._arch !== b._arch) return a._arch - b._arch;
      return (b._ts || 0) - (a._ts || 0);
    });
    if (q) list = list.filter(a => a._blob?.includes(q));
    if (falltypFilter !== "__all__") {
      list = list.filter(a => String(a.falltyp || "NORMAL").toUpperCase() === falltypFilter);
    }
    return list;
  }, [auftraege, deferredSuche, falltypFilter]);
  const aktive = gefiltert.filter(a => !a._arch);
  const archivierte = gefiltert.filter(a => a._arch);
  const falltypStats = useMemo(() => countFalltypAktivArchiv(auftraege), [auftraege]);
  const aktivTotal = useMemo(() => auftraege.filter((a) => !a._arch).length, [auftraege]);
  const archivTotal = useMemo(() => auftraege.filter((a) => a._arch).length, [auftraege]);
  const falltypHeader = useMemo(
    () => formatFalltypAktivArchivHeader(falltypStats, { aktiv: aktivTotal, archiv: archivTotal }),
    [falltypStats, aktivTotal, archivTotal]
  );
  const infoRight = falltypHeader.short;
  const panelId = objektId ? "objekt-services-panel" : "kunden-service-panel";
  const persistKey = objektId
    ? `objekt:${objektId}:service:open`
    : `kunden:${kundenId}:service:open`;
  const openAuftrag = useCallback(a => {
    const id = a?.id;
    if (!id) return;
    navigate(`/service/${id}`);
  }, [navigate]);
  return <CollapsibleBlock title={title} compact={compact} headerInfo={infoRight} headerInfoTitle={falltypHeader.title} panelId={panelId} contentClassName={compact ? "px-3 sm:px-4 pb-3 sm:pb-4" : "px-4 sm:px-6 pb-4 sm:pb-5"} defaultOpen={false} persistKey={persistKey}>
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1 max-sm:-mx-4 max-sm:px-4">
              <Search className="w-4 h-4 absolute left-7 max-sm:left-7 sm:left-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted" />
              <Input type="text" placeholder="Suche nach ID, Titel, Status…" value={suche} onChange={e => setSuche(e.target.value)} className="w-full pl-9" aria-label="Serviceaufträge durchsuchen" />
            </div>
            <div className="flex flex-wrap gap-1 shrink-0 max-sm:px-0" role="group" aria-label="Falltyp filtern">
              {[{
          value: "__all__",
          label: "Alle"
        }, ...FALLTYP_VALUES.filter(v => v !== "NORMAL").map(v => ({
          value: v,
          label: FALLTYP_LABELS[v]
        }))].map(opt => <AppButton key={opt.value} type="button" variant="ghost" onClick={() => setFalltypFilter(opt.value === falltypFilter ? "__all__" : opt.value)} className={`px-2 py-1 !h-8 rounded text-[11px] border transition ${falltypFilter === opt.value ? "seg-btn-active-primary font-semibold" : "seg-btn-idle"}`}>
                  {opt.label}
                </AppButton>)}
            </div>
          </div>

          {/* Inhalt */}
          {loading ? <SkeletonList /> : auftraege.length === 0 ? <p className="text-muted">Keine Serviceaufträge vorhanden.</p> : <>
              {aktive.length > 0 && <>
                  <h3 className="text-base font-semibold mt-2 mb-2 text-primary">Aktive Aufträge ({aktive.length})</h3>
                  <AuftragListe items={aktive} onOpen={openAuftrag} />
                </>}

              <div className="mt-6">
                <AppButton type="button" variant="ghost" onClick={() => setShowArchiv(v => !v)} className="w-full flex items-center justify-between border border-default rounded-lg px-3 py-2 hover:bg-muted text-primary" aria-expanded={showArchiv}>
                  <span className="font-semibold">Archivierte Aufträge</span>
                  <span className="text-xs bg-muted border border-default rounded px-2 py-0.5 text-muted">{archivierte.length}</span>
                </AppButton>
                {showArchiv && <div className="mt-3">
                    {archivierte.length === 0 ? <p className="text-muted text-sm">Keine archivierten Aufträge.</p> : <AuftragListe items={archivierte} onOpen={openAuftrag} />}
                  </div>}
              </div>

              {aktive.length === 0 && archivierte.length === 0 && <p className="text-muted mt-4">Keine Treffer zur Suche.</p>}
            </>}
    </CollapsibleBlock>;
}

function AuftragListe({
  items,
  onOpen
}) {
  const needsScroll = items.length > 3;
  const liCls = "border border-default rounded-lg p-3 flex justify-between items-center hover:bg-muted cursor-pointer gap-3 interactive-card";
  return <div className={`relative ${needsScroll ? "max-h-64 overflow-y-auto pr-1" : ""} max-sm:-mx-4 max-sm:px-4 cv-auto`} role="list">
      <ul className="space-y-2">
        {items.map(a => <li key={a.id} onClick={() => onOpen(a)} className={liCls} role="listitem" aria-label={`Serviceauftrag ${a.titel || a.name || `#${a.id}`}`}>
            <div className="min-w-0">
              <div className="font-semibold truncate text-primary">{a.titel || a.name || `Serviceauftrag #${a.id}`}</div>
              <div className="text-sm italic truncate text-muted">{a.beschreibung || "–"}</div>
            </div>
            <div className="flex gap-2 items-center shrink-0">
              <FalltypBadge falltyp={a.falltyp} size="xs" />
              <StatusBadge status={a.status} />
              {a.typ && <TypBadge typ={a.typ} />}
            </div>
          </li>)}
      </ul>
    </div>;
}

/* Skeleton für Ladezustand */
function SkeletonList() {
  const boxCls = "border border-default rounded-lg p-3 bg-muted animate-pulse";
  const barCls = "bg-divider";
  return <div className="space-y-2">
      {[1, 2, 3, 4].map(i => <div key={i} className={boxCls}>
          <div className={`h-4 w-1/3 rounded ${barCls}`} />
          <div className={`h-3 w-2/3 rounded mt-2 ${barCls}`} />
        </div>)}
    </div>;
}